import * as React from 'react';
import { InputAdornment, InputGroup, NumberInput } from '@cian/ui-kit';
import { useEventCallback } from '@cian/react-utils';

type TValueRaw = React.ChangeEvent<HTMLInputElement> | number | undefined;

export interface IRangeInputProps {
  spliced?: boolean;
  maxWidth?: number;
  postfix?: string;
  min?: number;
  max?: number;
  valueMin: number | undefined;
  valueMax: number | undefined;
  onChange(min: number | undefined, max: number | undefined): void;
}

export const RangeInput: React.FC<IRangeInputProps> = ({
  spliced,
  maxWidth,
  postfix,
  min,
  max,
  // HACK при передачи undefined в Input, он не сбрасывает свое значение в рантайме, только при перемаунте
  // oxlint-disable-next-line typescript/no-explicit-any
  valueMin = '' as any,
  // oxlint-disable-next-line typescript/no-explicit-any
  valueMax = '' as any,
  onChange,
}) => {
  // Необходимо для принудительного ре-рендеринга инпута верхней границы
  // при изменении нижней границы
  const [maxKey, setMaxKey] = React.useState(false);
  const rightAdornment = postfix && <InputAdornment>{postfix}</InputAdornment>;

  const parseValue = useEventCallback((valueRaw: TValueRaw) => {
    if (typeof valueRaw === 'number') {
      return valueRaw;
    } else {
      return valueRaw ? Number(valueRaw.target.value) : undefined;
    }
  });

  // Если нижняя границы становится больше верхней, то увеличиваем верхнюю
  const handleChangeMin = useEventCallback((valueRaw: TValueRaw) => {
    const parsedValueMin = parseValue(valueRaw);
    const parsedValueMax = parseValue(valueMax);
    const newValueMax =
      parsedValueMin === undefined || parsedValueMax === undefined
        ? parsedValueMax
        : Math.max(parsedValueMin, parsedValueMax);

    onChange(parsedValueMin, newValueMax);
    setMaxKey(!maxKey);
  });

  const handleChangeMax = useEventCallback((valueRaw: TValueRaw) => {
    onChange(parseValue(valueMin), parseValue(valueRaw));
  });

  // Минимальное значение верхней границы не может быть меньше нижней границы
  const minValueTo = React.useMemo(() => {
    const parsedValueMin = parseValue(valueMin);

    return parsedValueMin || min;
  }, [parseValue, valueMin, min]);

  return (
    <InputGroup maxWidth={maxWidth} spliced={spliced}>
      <NumberInput
        aria-label="От"
        placeholder="от"
        rightAdornment={rightAdornment}
        value={valueMin as number}
        onChange={handleChangeMin}
        min={min}
        max={max}
      />
      <NumberInput
        key={String(maxKey)}
        aria-label="До"
        placeholder="до"
        rightAdornment={rightAdornment}
        value={valueMax as number}
        onChange={handleChangeMax}
        min={minValueTo}
        max={max}
      />
    </InputGroup>
  );
};
